import type { Profile, ProfileState } from '@/types';
import { ONE_CONTEXT_PROFILE_CONNECTION, ONE_CONTEXT_TENANT } from '@/utilities/constants';
import { PL_LOG_GROUPS, logger } from '@/utilities/logger';
import { type AuthenticationState, isLoggedIn } from '@b2x/authentication-library';
import { LOG_LEVEL, PROFILE_CONTEXT_KEYS, useTranslations } from '@b2x/shared';
import { CONNECTIONS, type Connection, getConnectionDispatchers, getConnectionState } from '@b2x/shared';
import type { HeaderDispatchers, HeaderNavigationItemsConfiguration } from '@dh-io-hp/header-connection';
import type { ComponentsState } from '@owc/component-registry-connection';
import { subscribe } from '@seamless/one-context';

export async function updateNavigationItem() {
  const userMenuConfig = await getUserMenuConfig();
  const headerDispatchers = await getConnectionDispatchers<HeaderDispatchers>('HP_HEADER_CONNECTION');

  if (!headerDispatchers || Object.keys(headerDispatchers).length === 0) {
    logger('No header dispatchers found.', {
      args: [],
      group: PL_LOG_GROUPS.NAVIGATION_ITEM,
      level: LOG_LEVEL.WARN,
    });
    return;
  }

  subscribe(ONE_CONTEXT_PROFILE_CONNECTION, {
    tenant: ONE_CONTEXT_TENANT,
    componentName: 'PL',
    callback: async (profileState: ProfileState) => {
      const authState = await getConnectionState<AuthenticationState>(CONNECTIONS.AUTH_CONNECTION_NAME as Connection);
      const { isB2BModeEnabled } = userMenuConfig;
      const profileToDisplay = getProfileToDisplay(profileState, isB2BModeEnabled);

      const navigationItemConfig = getNavItemConfig(authState, profileToDisplay, userMenuConfig);

      headerDispatchers.setNavigationItemsConfiguration(navigationItemConfig);

      logger('Update OneHeader navigation item with', {
        args: [navigationItemConfig.at(0)],
        group: PL_LOG_GROUPS.NAVIGATION_ITEM,
        level: LOG_LEVEL.INFO,
      });
    },
  });
}

interface UmConfig extends ComponentsState {
  payload?: {
    loginLabel?: string;
    userProfileAria?: string;
    isProfileSelectionEnabled?: boolean;
    menuEntriesPrivate?: Record<string, unknown>;
    overviewEntriesPrivate?: Record<string, unknown>;
    settingsEntriesPrivate?: Record<string, unknown>;
    isB2BModeEnabled?: boolean;
  };
}

async function getUserMenuConfig() {
  // header v1 config
  let umConfig = Object.values(window.aemNamespace.componentData).find((component: any) => {
    const payload = component?.payload;
    return (
      payload &&
      (payload.menuEntriesPrivate ||
        payload.overviewEntriesPrivate ||
        payload.settingsEntriesPrivate ||
        payload.userProfileAria)
    );
  })?.payload;

  // ssr header
  if (!umConfig) {
    umConfig = Object.values(window.aemNamespace.componentData).find((component: any) => {
      return component.payload?.userMenu;
    })?.payload.userMenu.data.payload;
  }

  return {
    loginLabel: umConfig?.loginLabel || 'Login',
    isProfileSelectionEnabled: Boolean(umConfig?.isProfileSelectionEnabled),
    isB2BModeEnabled: Boolean(umConfig?.isB2BModeEnabled),
  };
}

function getNavItemConfig(
  authState: AuthenticationState,
  activeProfile: Profile,
  menuConfig: UmConfig,
): HeaderNavigationItemsConfiguration {
  const { loginLabel, isProfileSelectionEnabled } = menuConfig;

  const isUserLoggedIn = isLoggedIn(authState);
  const label = isUserLoggedIn ? getLoggedInLabel(activeProfile) : loginLabel;
  const subLabel = isProfileSelectionEnabled ? getSubLabel(activeProfile, isUserLoggedIn) : undefined;
  const fullName = getFullName(activeProfile);
  const icon = getIcon(activeProfile);
  const image = activeProfile.profilePicture;

  return [
    {
      name: 'iam-user-menu-v3',
      label,
      subLabel,
      avatar: {
        fullName,
        icon,
        image,
      },
    },
  ];
}

function getProfileToDisplay(profileState: ProfileState, isB2BModeEnabled: boolean): Profile {
  if (profileState.activeProfile.businessType === 'B2C' || isB2BModeEnabled) {
    return profileState.activeProfile;
  }

  return (
    profileState.availableProfiles.find((profile) => {
      return profile.businessType === 'B2C';
    }) || profileState.activeProfile
  );
}

function getLoggedInLabel(activeProfile: Profile) {
  const { t } = useTranslations();

  if (activeProfile.businessType === 'B2C') {
    return activeProfile.displayName || t(PROFILE_CONTEXT_KEYS.PRIVATE);
  }

  return activeProfile.displayName || t(PROFILE_CONTEXT_KEYS.GUEST_COMPANY);
}

function getSubLabel(activeProfile: Profile, isLoggedIn: boolean) {
  const { t } = useTranslations();
  const isB2B = activeProfile.businessType === 'B2B';
  const hasDisplayName = Boolean(activeProfile.displayName);

  if (isLoggedIn && !hasDisplayName) {
    return undefined;
  }

  return isB2B ? t(PROFILE_CONTEXT_KEYS.GUEST_COMPANY) : t(PROFILE_CONTEXT_KEYS.GUEST_PRIVATE);
}

function getFullName(activeProfile: Profile) {
  if (!activeProfile.displayName) {
    return '';
  }

  if (activeProfile.businessType === 'B2C') {
    return activeProfile.displayName;
  }

  return activeProfile.displayName.charAt(0);
}

function getIcon(activeProfile: Profile) {
  return activeProfile.businessType === 'B2C' ? 'bds/user/24' : 'company/24';
}
